/**
 * Ada Tool Definitions and Executor
 *
 * Implements Claude tool use for agentic AI capabilities.
 * Ada can dynamically query vector_db and other canisters to find information.
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Tool definitions separate from execution
 * - Open/Closed: Easy to add new tools without modifying existing code
 * - Strategy Pattern: Each tool is a strategy for data retrieval
 */

import { EAImpactAnalyzer } from '../ea/ea-impact-analyzer';

// ============================================================================
// Tool Definitions (Claude Format)
// ============================================================================

export interface ToolDefinition {
    name: string;
    description: string;
    input_schema: {
        type: 'object';
        properties: Record<string, {
            type: string;
            description: string;
            enum?: string[];
        }>;
        required: string[];
    };
}

/**
 * Tools available to Ada for querying infrastructure data
 */
export const ADA_TOOLS: ToolDefinition[] = [
    {
        name: 'search_configs',
        description: 'Search configuration files by keyword or service type. Use this to find devices running specific software like nginx, postgres, apache, mysql, redis, docker, etc. Returns list of devices with matching configs.',
        input_schema: {
            type: 'object',
            properties: {
                keyword: {
                    type: 'string',
                    description: 'Keyword to search for in config paths and types (e.g., "nginx", "postgres", "systemd")'
                }
            },
            required: ['keyword']
        }
    },
    {
        name: 'search_devices',
        description: 'Search devices by various criteria. Use this to find devices by IP, hostname, OS, device type, or status.',
        input_schema: {
            type: 'object',
            properties: {
                query: {
                    type: 'string',
                    description: 'Search query - can be partial IP, hostname, OS name, device type, or status'
                },
                filter_type: {
                    type: 'string',
                    description: 'Type of filter to apply',
                    enum: ['ip', 'hostname', 'os', 'device_type', 'status', 'any']
                }
            },
            required: ['query']
        }
    },
    {
        name: 'get_device_details',
        description: 'Get detailed information about a specific device including all metadata, config files, and inventory data.',
        input_schema: {
            type: 'object',
            properties: {
                device_id: {
                    type: 'string',
                    description: 'The device ID or IP address to get details for'
                }
            },
            required: ['device_id']
        }
    },
    {
        name: 'get_config_content',
        description: 'Get the actual content of a specific configuration file. Use this when you need to see what\'s inside a config file.',
        input_schema: {
            type: 'object',
            properties: {
                config_id: {
                    type: 'string',
                    description: 'The config ID (format: config_IP__path_to_file)'
                }
            },
            required: ['config_id']
        }
    },
    {
        name: 'list_all_services',
        description: 'List all discovered software/services across all devices. Returns service types with device counts and IPs.',
        input_schema: {
            type: 'object',
            properties: {},
            required: []
        }
    },
    {
        name: 'get_device_statistics',
        description: 'Get summary statistics about all devices - total count, breakdown by type, OS, and status.',
        input_schema: {
            type: 'object',
            properties: {},
            required: []
        }
    },
    {
        name: 'get_service_maps',
        description: 'Get information about service maps and their device topology.',
        input_schema: {
            type: 'object',
            properties: {
                service_name: {
                    type: 'string',
                    description: 'Optional: specific service map name to get details for. If not provided, lists all service maps.'
                }
            },
            required: []
        }
    },
    // === Enterprise Architecture Tools ===
    {
        name: 'get_ea_statistics',
        description: 'Get aggregate EA statistics: total counts of organizations, applications, services, data objects, HR roles, headcount by division, and application criticality distribution. Use this for high-level enterprise overview questions.',
        input_schema: {
            type: 'object',
            properties: {},
            required: []
        }
    },
    {
        name: 'query_ea_organizations',
        description: 'Query organizational structure. Returns divisions, departments, teams with headcount and reporting lines. Use this for questions about org structure, headcount, teams, or divisions.',
        input_schema: {
            type: 'object',
            properties: {
                division: {
                    type: 'string',
                    description: 'Optional: filter by division name (e.g., "Cards", "Front Office", "Technology")'
                },
                department: {
                    type: 'string',
                    description: 'Optional: filter by department name'
                }
            },
            required: []
        }
    },
    {
        name: 'query_ea_applications',
        description: 'Query the application portfolio. Returns applications with vendor, type, domain, criticality, user count, and integrations. Use this for questions about applications, vendors, or application risk.',
        input_schema: {
            type: 'object',
            properties: {
                domain: {
                    type: 'string',
                    description: 'Optional: filter by domain (e.g., "Cards", "Lending", "Banking")'
                },
                criticality: {
                    type: 'string',
                    description: 'Optional: filter by criticality level',
                    enum: ['Critical', 'High', 'Medium', 'Low']
                },
                vendor: {
                    type: 'string',
                    description: 'Optional: filter by vendor name'
                }
            },
            required: []
        }
    },
    {
        name: 'get_ea_impact_analysis',
        description: 'Analyze downstream impact of changes to an entity. Traverses the relationship graph to find all affected entities, services, processes, headcount, SLAs at risk, and data at risk. Use this for "what if" questions like "what happens if we decommission X?" or "what\'s affected if system Y goes down?".',
        input_schema: {
            type: 'object',
            properties: {
                entity_type: {
                    type: 'string',
                    description: 'Type of entity to analyze impact for',
                    enum: ['application', 'technical_service', 'business_service', 'data_object', 'organization']
                },
                entity_id: {
                    type: 'string',
                    description: 'ID of the entity to analyze (e.g., app ID, service ID)'
                }
            },
            required: ['entity_type', 'entity_id']
        }
    },
    {
        name: 'query_ea_relationships',
        description: 'Find all relationships for an entity across the EA model. Shows how entities are connected: integrations, dependencies, ownership, and usage. Use this to understand entity connections and dependencies.',
        input_schema: {
            type: 'object',
            properties: {
                source_type: {
                    type: 'string',
                    description: 'Type of the source entity',
                    enum: ['application', 'technical_service', 'business_service', 'data_object', 'organization', 'hr_role']
                },
                source_id: {
                    type: 'string',
                    description: 'ID of the entity to find relationships for'
                }
            },
            required: ['source_type', 'source_id']
        }
    }
];

// ============================================================================
// Tool Executor
// ============================================================================

export interface ToolResult {
    success: boolean;
    data: any;
    error?: string;
}

/**
 * Convert metadata from vector_db tuple format to object.
 * vector_db returns metadata as Array<[string, string]>, not as an object.
 */
function metadataToObject(metadataTuples: any): Record<string, string> {
    const obj: Record<string, string> = {};
    if (Array.isArray(metadataTuples)) {
        for (const tuple of metadataTuples) {
            if (Array.isArray(tuple) && tuple.length >= 2) {
                obj[tuple[0]] = tuple[1];
            }
        }
    } else if (metadataTuples && typeof metadataTuples === 'object') {
        // Already an object (proxy adapter may return this)
        return metadataTuples;
    }
    return obj;
}

/**
 * Executes tools by calling the appropriate canister methods
 */
export class AdaToolExecutor {
    private canisterClient: any;

    constructor() {
        this.canisterClient = (window as any).canisterClient;
    }

    /**
     * Execute a tool call and return results
     */
    async execute(toolName: string, toolInput: Record<string, any>): Promise<ToolResult> {
        console.log(`AdaToolExecutor: Executing ${toolName}`, toolInput);

        if (!this.canisterClient) {
            return { success: false, data: null, error: 'Canister client not available' };
        }

        try {
            switch (toolName) {
                case 'search_configs':
                    return await this.searchConfigs(toolInput.keyword);
                case 'search_devices':
                    return await this.searchDevices(toolInput.query, toolInput.filter_type || 'any');
                case 'get_device_details':
                    return await this.getDeviceDetails(toolInput.device_id);
                case 'get_config_content':
                    return await this.getConfigContent(toolInput.config_id);
                case 'list_all_services':
                    return await this.listAllServices();
                case 'get_device_statistics':
                    return await this.getDeviceStatistics();
                case 'get_service_maps':
                    return await this.getServiceMaps(toolInput.service_name);
                // Enterprise Architecture tools
                case 'get_ea_statistics':
                    return await this.getEAStatistics();
                case 'query_ea_organizations':
                    return await this.queryEAOrganizations(toolInput.division, toolInput.department);
                case 'query_ea_applications':
                    return await this.queryEAApplications(toolInput.domain, toolInput.criticality, toolInput.vendor);
                case 'get_ea_impact_analysis':
                    return await this.getEAImpactAnalysis(toolInput.entity_type, toolInput.entity_id);
                case 'query_ea_relationships':
                    return await this.queryEARelationships(toolInput.source_type, toolInput.source_id);
                default:
                    return { success: false, data: null, error: `Unknown tool: ${toolName}` };
            }
        } catch (error: any) {
            console.error(`AdaToolExecutor: Error executing ${toolName}:`, error);
            return { success: false, data: null, error: error.message || 'Tool execution failed' };
        }
    }

    /**
     * Search configs by keyword
     */
    private async searchConfigs(keyword: string): Promise<ToolResult> {
        const configsResult = await this.canisterClient.callCanister('vector_db', 'get_all_configs', []);
        const configs = configsResult || [];

        if (!Array.isArray(configs)) {
            return { success: false, data: null, error: 'Failed to fetch configs' };
        }

        const keywordLower = keyword.toLowerCase();
        const matchingConfigs: Array<{ip: string, path: string, type: string}> = [];
        const deviceIPs = new Set<string>();

        for (const config of configs) {
            const configId = (config[0] || '').toLowerCase();
            const ip = config[1] || '';
            const path = (config[2] || '').toLowerCase();
            const configType = (config[3] || '').toLowerCase();

            if (configId.includes(keywordLower) ||
                path.includes(keywordLower) ||
                configType.includes(keywordLower)) {
                matchingConfigs.push({ ip, path: config[2], type: config[3] });
                deviceIPs.add(ip);
            }
        }

        return {
            success: true,
            data: {
                keyword,
                matches: matchingConfigs.length,
                devices: Array.from(deviceIPs),
                device_count: deviceIPs.size,
                config_files: matchingConfigs.slice(0, 20) // Limit to 20 for context size
            }
        };
    }

    /**
     * Search devices by criteria
     */
    private async searchDevices(query: string, filterType: string): Promise<ToolResult> {
        const devicesResult = await this.canisterClient.getAllDevices();
        const devices = devicesResult?.ok || devicesResult || [];

        if (!Array.isArray(devices)) {
            return { success: false, data: null, error: 'Failed to fetch devices' };
        }

        const queryLower = query.toLowerCase();
        const matchingDevices: Array<{id: string, ip: string, hostname: string, os: string, type: string, status: string}> = [];

        for (const device of devices) {
            const id = device[0] || '';
            const metadata = metadataToObject(device[1]);
            const ip = metadata.ip || '';
            const hostname = metadata.hostname || id;
            const os = metadata.os || '';
            const deviceType = metadata.device_type || '';
            const status = metadata.status || '';

            let matches = false;
            switch (filterType) {
                case 'ip':
                    matches = ip.toLowerCase().includes(queryLower);
                    break;
                case 'hostname':
                    matches = hostname.toLowerCase().includes(queryLower);
                    break;
                case 'os':
                    matches = os.toLowerCase().includes(queryLower);
                    break;
                case 'device_type':
                    matches = deviceType.toLowerCase().includes(queryLower);
                    break;
                case 'status':
                    matches = status.toLowerCase().includes(queryLower);
                    break;
                case 'any':
                default:
                    matches = ip.toLowerCase().includes(queryLower) ||
                              hostname.toLowerCase().includes(queryLower) ||
                              os.toLowerCase().includes(queryLower) ||
                              deviceType.toLowerCase().includes(queryLower) ||
                              status.toLowerCase().includes(queryLower);
            }

            if (matches) {
                matchingDevices.push({ id, ip, hostname, os, type: deviceType, status });
            }
        }

        return {
            success: true,
            data: {
                query,
                filter_type: filterType,
                matches: matchingDevices.length,
                devices: matchingDevices
            }
        };
    }

    /**
     * Get detailed device information
     */
    private async getDeviceDetails(deviceId: string): Promise<ToolResult> {
        // Get device metadata
        const devicesResult = await this.canisterClient.getAllDevices();
        const devices = devicesResult?.ok || devicesResult || [];

        let deviceData: any = null;
        for (const device of devices) {
            const id = device[0] || '';
            const metadata = metadataToObject(device[1]);
            const ip = metadata.ip || '';

            if (id === deviceId || ip === deviceId || metadata.hostname === deviceId) {
                deviceData = { id, ...metadata };
                break;
            }
        }

        if (!deviceData) {
            return { success: false, data: null, error: `Device not found: ${deviceId}` };
        }

        // Get configs for this device
        const configsResult = await this.canisterClient.callCanister('vector_db', 'get_all_configs', []);
        const configs = configsResult || [];
        const deviceConfigs = configs
            .filter((c: any) => c[1] === deviceData.ip)
            .map((c: any) => ({ path: c[2], type: c[3] }));

        return {
            success: true,
            data: {
                device: deviceData,
                config_files: deviceConfigs,
                config_count: deviceConfigs.length
            }
        };
    }

    /**
     * Get config file content
     */
    private async getConfigContent(configId: string): Promise<ToolResult> {
        try {
            const result = await this.canisterClient.callCanister('vector_db', 'get_config_by_id', [configId]);
            // Result is opt ConfigEmbedding - array format: [] for None, [value] for Some
            const configData = Array.isArray(result) && result.length > 0 ? result[0] : result;
            if (configData) {
                return {
                    success: true,
                    data: {
                        config_id: configData.config_id || configId,
                        content: configData.content || '',
                        path: configData.path || '',
                        type: configData.config_type || '',
                        ip: configData.ip || ''
                    }
                };
            }
            return { success: false, data: null, error: `Config not found: ${configId}` };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    /**
     * List all discovered services
     */
    private async listAllServices(): Promise<ToolResult> {
        const configsResult = await this.canisterClient.callCanister('vector_db', 'get_all_configs', []);
        const configs = configsResult || [];

        const servicesByType: Record<string, Set<string>> = {};

        for (const config of configs) {
            const ip = config[1] || '';
            const configType = config[3] || 'unknown';

            if (!servicesByType[configType]) {
                servicesByType[configType] = new Set();
            }
            servicesByType[configType].add(ip);
        }

        const services = Object.entries(servicesByType).map(([type, ips]) => ({
            service_type: type,
            device_count: ips.size,
            devices: Array.from(ips)
        }));

        return {
            success: true,
            data: {
                total_service_types: services.length,
                services: services.sort((a, b) => b.device_count - a.device_count)
            }
        };
    }

    /**
     * Get device statistics
     */
    private async getDeviceStatistics(): Promise<ToolResult> {
        const devicesResult = await this.canisterClient.getAllDevices();
        const devices = devicesResult?.ok || devicesResult || [];

        const stats = {
            total: devices.length,
            by_type: {} as Record<string, number>,
            by_os: {} as Record<string, number>,
            by_status: {} as Record<string, number>
        };

        for (const device of devices) {
            const metadata = metadataToObject(device[1]);
            const deviceType = metadata.device_type || 'Unknown';
            const os = metadata.os || 'Unknown';
            const status = metadata.status || 'Unknown';

            stats.by_type[deviceType] = (stats.by_type[deviceType] || 0) + 1;
            stats.by_os[os] = (stats.by_os[os] || 0) + 1;
            stats.by_status[status] = (stats.by_status[status] || 0) + 1;
        }

        return { success: true, data: stats };
    }

    /**
     * Get service maps
     */
    private async getServiceMaps(serviceName?: string): Promise<ToolResult> {
        try {
            const namesResult = await this.canisterClient.callCanister('vector_db', 'list_service_map_names', []);
            const serviceMapNames = namesResult || [];

            if (!serviceName) {
                return {
                    success: true,
                    data: {
                        total_service_maps: serviceMapNames.length,
                        service_maps: serviceMapNames
                    }
                };
            }

            // Get specific service map details
            const mapResult = await this.canisterClient.callCanister('vector_db', 'get_service_map', [serviceName]);
            if (mapResult) {
                return {
                    success: true,
                    data: {
                        service_map: mapResult
                    }
                };
            }

            return { success: false, data: null, error: `Service map not found: ${serviceName}` };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    // ========================================================================
    // Enterprise Architecture Tool Handlers
    // ========================================================================

    /**
     * Get EA statistics overview
     */
    private async getEAStatistics(): Promise<ToolResult> {
        try {
            const stats = await this.canisterClient.callCanister('vector_db', 'get_ea_statistics', []);
            return { success: true, data: stats };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    /**
     * Query EA organizations with optional division/department filter
     */
    private async queryEAOrganizations(division?: string, department?: string): Promise<ToolResult> {
        try {
            const orgs = await this.canisterClient.callCanister('vector_db', 'list_ea_organizations', []);
            if (!Array.isArray(orgs)) {
                return { success: false, data: null, error: 'Failed to fetch organizations' };
            }

            let filtered = orgs;
            if (division) {
                const divLower = division.toLowerCase();
                filtered = filtered.filter((o: any) =>
                    (o.division || '').toLowerCase().includes(divLower)
                );
            }
            if (department) {
                const deptLower = department.toLowerCase();
                filtered = filtered.filter((o: any) =>
                    (o.department || '').toLowerCase().includes(deptLower)
                );
            }

            // Summarize for context efficiency
            const totalHeadcount = filtered.reduce((sum: number, o: any) => sum + (o.headcount || 0), 0);
            const divisions = [...new Set(filtered.map((o: any) => o.division))];

            return {
                success: true,
                data: {
                    total_matching: filtered.length,
                    total_headcount: totalHeadcount,
                    divisions,
                    organizations: filtered.slice(0, 50).map((o: any) => ({
                        org_id: o.org_id,
                        division: o.division,
                        department: o.department,
                        team: o.team,
                        headcount: o.headcount,
                        location_type: o.location_type,
                        reports_to: o.reports_to,
                    }))
                }
            };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    /**
     * Query EA applications with optional filters
     */
    private async queryEAApplications(domain?: string, criticality?: string, vendor?: string): Promise<ToolResult> {
        try {
            const apps = await this.canisterClient.callCanister('vector_db', 'list_ea_applications', []);
            if (!Array.isArray(apps)) {
                return { success: false, data: null, error: 'Failed to fetch applications' };
            }

            let filtered = apps;
            if (domain) {
                const domLower = domain.toLowerCase();
                filtered = filtered.filter((a: any) =>
                    (a.domain || '').toLowerCase().includes(domLower)
                );
            }
            if (criticality) {
                const critLower = criticality.toLowerCase();
                filtered = filtered.filter((a: any) =>
                    (a.criticality || '').toLowerCase() === critLower
                );
            }
            if (vendor) {
                const vendorLower = vendor.toLowerCase();
                filtered = filtered.filter((a: any) =>
                    (a.vendor || '').toLowerCase().includes(vendorLower)
                );
            }

            const totalUsers = filtered.reduce((sum: number, a: any) => sum + (a.users || 0), 0);

            return {
                success: true,
                data: {
                    total_matching: filtered.length,
                    total_users: totalUsers,
                    applications: filtered.slice(0, 30).map((a: any) => ({
                        app_id: a.app_id,
                        name: a.name,
                        vendor: a.vendor,
                        app_type: a.app_type,
                        domain: a.domain,
                        users: a.users,
                        criticality: a.criticality,
                        integration_method: a.integration_method,
                        integrates_with: a.integrates_with,
                    }))
                }
            };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    /**
     * Perform EA impact analysis using the EAImpactAnalyzer
     */
    private async getEAImpactAnalysis(entityType: string, entityId: string): Promise<ToolResult> {
        try {
            const analyzer = new EAImpactAnalyzer(this.canisterClient);
            const report = await analyzer.analyzeImpact(entityType, entityId);
            return { success: true, data: report };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }

    /**
     * Query EA relationships for a specific entity
     */
    private async queryEARelationships(sourceType: string, sourceId: string): Promise<ToolResult> {
        try {
            const rels = await this.canisterClient.callCanister('vector_db', 'list_ea_relationships', []);
            if (!Array.isArray(rels)) {
                return { success: false, data: null, error: 'Failed to fetch relationships' };
            }

            const matching = rels.filter((r: any) =>
                r.source_id === sourceId || r.target_id === sourceId
            );

            return {
                success: true,
                data: {
                    entity: { type: sourceType, id: sourceId },
                    total_relationships: matching.length,
                    relationships: matching.map((r: any) => ({
                        source_type: r.source_type,
                        source_id: r.source_id,
                        target_type: r.target_type,
                        target_id: r.target_id,
                        relationship_type: r.relationship_type,
                    }))
                }
            };
        } catch (error: any) {
            return { success: false, data: null, error: error.message };
        }
    }
}

// ============================================================================
// Agentic Chat Handler
// ============================================================================

const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';
const CLAUDE_MODEL = 'claude-sonnet-4-20250514';
const MAX_TOOL_ITERATIONS = 10;

export interface AgenticChatResult {
    response: string;
    toolsUsed: string[];
    error?: string;
}

/**
 * Handles agentic chat with tool use
 */
export class AdaAgenticChat {
    private toolExecutor: AdaToolExecutor;
    private apiKey: string | null = null;

    constructor() {
        this.toolExecutor = new AdaToolExecutor();
    }

    /**
     * Set the API key for Claude calls
     */
    setApiKey(apiKey: string): void {
        this.apiKey = apiKey;
    }

    /**
     * Process a user query with tool use capabilities
     */
    async chat(userQuery: string, systemContext?: string): Promise<AgenticChatResult> {
        if (!this.apiKey) {
            // Try to get API key from canister
            try {
                const canisterClient = (window as any).canisterClient;
                if (canisterClient) {
                    const result = await canisterClient.getApiKeyForFrontend();
                    if ('Ok' in result) {
                        this.apiKey = result.Ok;
                    }
                }
            } catch (e) {
                console.error('Failed to get API key:', e);
            }
        }

        if (!this.apiKey) {
            return { response: 'API key not configured', toolsUsed: [], error: 'No API key' };
        }

        const systemPrompt = `You are Ada, an AI assistant for the Agentic Service Discovery (ASD) system.
You help users understand their enterprise infrastructure by querying real data from the vector database.

You have access to tools that let you search and retrieve information about:
- Devices (servers, network equipment, storage)
- Configuration files (nginx, postgres, systemd, etc.)
- Services and software installed on devices
- Service maps showing application topology
- Enterprise Architecture: organizations, divisions, departments, headcount
- Application portfolio: vendors, criticality, integrations, user counts
- Business and technical services with SLAs
- Data objects with classifications and retention policies
- Impact analysis: downstream effects of decommissioning or changing any entity

IMPORTANT: When asked about specific software, services, devices, or enterprise architecture, USE THE TOOLS to find accurate information.
Don't guess or make assumptions - query the data.

For CIO-level strategic questions (cost reduction, risk analysis, vendor consolidation, headcount impact), use the EA tools:
- get_ea_statistics for high-level overview
- query_ea_organizations for org structure and headcount
- query_ea_applications for application portfolio analysis
- get_ea_impact_analysis for "what if" scenarios
- query_ea_relationships for dependency mapping

${systemContext || ''}`;

        const messages: Array<{role: string, content: any}> = [
            { role: 'user', content: userQuery }
        ];

        const toolsUsed: string[] = [];
        let iterations = 0;

        while (iterations < MAX_TOOL_ITERATIONS) {
            iterations++;

            const response = await this.callClaude(systemPrompt, messages);

            if (response.error) {
                return { response: '', toolsUsed, error: response.error };
            }

            // Check if Claude wants to use tools
            const toolUseBlocks = response.content.filter((block: any) => block.type === 'tool_use');

            if (toolUseBlocks.length === 0) {
                // No tool use - extract text response
                const textBlocks = response.content.filter((block: any) => block.type === 'text');
                const finalResponse = textBlocks.map((b: any) => b.text).join('\n');
                return { response: finalResponse, toolsUsed };
            }

            // Execute tools and add results to messages
            messages.push({ role: 'assistant', content: response.content });

            const toolResults: Array<{type: string, tool_use_id: string, content: string}> = [];

            for (const toolUse of toolUseBlocks) {
                console.log(`Ada executing tool: ${toolUse.name}`, toolUse.input);
                toolsUsed.push(toolUse.name);

                const result = await this.toolExecutor.execute(toolUse.name, toolUse.input);

                toolResults.push({
                    type: 'tool_result',
                    tool_use_id: toolUse.id,
                    content: JSON.stringify(result.data || { error: result.error })
                });
            }

            messages.push({ role: 'user', content: toolResults });
        }

        return {
            response: 'Maximum tool iterations reached. Please try a simpler query.',
            toolsUsed,
            error: 'Max iterations'
        };
    }

    /**
     * Call Claude API with tools
     */
    private async callClaude(
        systemPrompt: string,
        messages: Array<{role: string, content: any}>
    ): Promise<{content: any[], error?: string}> {
        try {
            const response = await fetch(CLAUDE_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.apiKey!,
                    'anthropic-version': '2023-06-01',
                    'anthropic-dangerous-direct-browser-access': 'true'
                },
                body: JSON.stringify({
                    model: CLAUDE_MODEL,
                    max_tokens: 4096,
                    system: systemPrompt,
                    tools: ADA_TOOLS,
                    messages: messages
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Claude API error:', errorText);
                return { content: [], error: `API error: ${response.status}` };
            }

            const data = await response.json();
            return { content: data.content };

        } catch (error: any) {
            console.error('Claude API call failed:', error);
            return { content: [], error: error.message };
        }
    }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const adaAgenticChat = new AdaAgenticChat();

// Expose for debugging
if (typeof window !== 'undefined') {
    (window as any).adaAgenticChat = adaAgenticChat;
    (window as any).adaToolExecutor = new AdaToolExecutor();
}

console.log('Ada Tools: Module loaded with', ADA_TOOLS.length, 'tools');
